<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import { NButton, NInput, NPopconfirm, NSelect } from 'naive-ui'
import type { AgentPrompt } from '../stores/agent_prompts'
import { promptFactoryButtonProps } from './PromptFactoryButton'

// The Prompt Library's editor pane: label + text for the prompt selected
// in the list, its scope, and Remove / Cancel / Save. The parent keys this
// component by prompt id, so switching prompts re-seeds the draft from
// scratch. Scope changes stay immediate (one discrete action); label/text
// edits go through the local draft and an explicit Save.
//
// `onUpdate` is a promise-returning prop (not an emit) because
// `draft.saving` must reflect the ACTUAL in-flight save — awaiting a plain
// emit resolves instantly, so the Save spinner would never show.
const props = defineProps<{
  prompt: AgentPrompt
  // naive-ui option values can't be `null` (see PromptLibrary.vue's
  // GLOBAL_SCOPE comment) — `''` is the Global sentinel here too.
  scopeOptions: Array<{ label: string; value: string }>
  onUpdate: (id: number, params: { label?: string; text?: string }) => Promise<void>
}>()

const emit = defineEmits<{
  scopeChange: [id: number, repoSlug: string | null]
  destroy: [id: number]
}>()

const draft = reactive({ label: props.prompt.label, text: props.prompt.text, saving: false })

// Gates Save/Cancel — no no-op API round-trip when nothing changed.
const isDirty = computed(() => draft.label !== props.prompt.label || draft.text !== props.prompt.text)

// The prompt can change underneath us (a store refetch after another tab
// edited it). Follow it while the draft is clean; never clobber unsaved
// edits — Save then deliberately overwrites with the user's version.
watch(
  () => [props.prompt.label, props.prompt.text] as const,
  ([label, text], [oldLabel, oldText]) => {
    if (draft.label !== oldLabel || draft.text !== oldText) return
    draft.label = label
    draft.text = text
  },
)

function cancel(): void {
  draft.label = props.prompt.label
  draft.text = props.prompt.text
}

async function save(): Promise<void> {
  if (!isDirty.value) return
  draft.saving = true
  try {
    await props.onUpdate(props.prompt.id, { label: draft.label, text: draft.text })
  } finally {
    draft.saving = false
  }
}

function onScopeChange(value: string): void {
  emit('scopeChange', props.prompt.id, value || null)
}
</script>

<template>
  <section
    aria-labelledby="prompt-editor-heading"
    class="flex flex-col gap-4 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
    :data-testid="`prompt-editor-${prompt.id}`"
  >
    <h2 id="prompt-editor-heading" class="m-0 text-lg font-semibold text-ink">Edit prompt</h2>

    <div class="flex flex-col gap-1.5">
      <label :for="`prompt-label-input-${prompt.id}`" class="font-medium text-ink">Label</label>
      <NInput
        v-model:value="draft.label"
        size="large"
        :input-props="{ id: `prompt-label-input-${prompt.id}` }"
        :data-testid="`prompt-label-${prompt.id}`"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label :for="`prompt-text-input-${prompt.id}`" class="font-medium text-ink">Prompt</label>
      <NInput
        v-model:value="draft.text"
        type="textarea"
        class="font-mono text-[13px]"
        :autosize="{ minRows: 6, maxRows: 16 }"
        :input-props="{ id: `prompt-text-input-${prompt.id}` }"
        :data-testid="`prompt-text-${prompt.id}`"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="font-medium text-ink">Scope</span>
      <NSelect
        :value="prompt.repo_slug || ''"
        :options="scopeOptions"
        aria-label="Scope"
        class="sm:max-w-72"
        :data-testid="`prompt-scope-${prompt.id}`"
        @update:value="onScopeChange"
      />
    </div>

    <div class="flex flex-wrap items-center gap-2.5">
      <NPopconfirm
        positive-text="Remove"
        negative-text="Cancel"
        :positive-button-props="{ type: 'error' }"
        @positive-click="emit('destroy', prompt.id)"
      >
        <template #trigger>
          <button
            type="button"
            class="min-h-11 cursor-pointer rounded-[10px] border-0 bg-transparent px-3.5 text-status-alert hover:bg-status-alert/10 sm:min-h-10"
            :data-testid="`prompt-delete-${prompt.id}`"
          >
            Remove
          </button>
        </template>
        Remove this prompt?
      </NPopconfirm>
      <span class="flex-1" />
      <button
        type="button"
        class="min-h-11 cursor-pointer rounded-[10px] border border-border-control bg-transparent px-4 text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-10"
        :disabled="!isDirty || draft.saving"
        :data-testid="`prompt-cancel-${prompt.id}`"
        @click="cancel"
      >
        Cancel
      </button>
      <NButton
        v-bind="promptFactoryButtonProps"
        class="min-h-11 px-4 font-semibold sm:min-h-10"
        :disabled="!isDirty"
        :loading="draft.saving"
        :data-testid="`prompt-save-${prompt.id}`"
        @click="save"
        >Save</NButton
      >
    </div>
  </section>
</template>
